import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";

import { useModule } from "@/hooks/use-module";

import { parseError, readErrorDetail } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { toast } from "@/lib/use-toast";

import {
  CoupaApprovalAction,
  CoupaApprovalRow,
  CoupaCommoditiesResponse,
  CoupaCommodityRuleResponse,
  CoupaConnectionSaveRequest,
  CoupaConnectionStatus,
  CoupaConnectionTestResult,
  CoupaDeliveryLine,
  CoupaDeliveryResult,
  CoupaFixDriftRequest,
  CoupaGoLiveChoice,
  CoupaGoLivePreview,
  CoupaGoLiveWarning,
  CoupaItemLinkRow,
  CoupaItemLinksResponse,
  CoupaItemLinksSaveRequest,
  CoupaItemMasterAddRequest,
  CoupaItemPrice,
  CoupaItemRuleResponse,
  CoupaItemRulesResponse,
  CoupaLineLinkOptions,
  CoupaLineLinkRequest,
  CoupaLineLinkResponse,
  CoupaLineReceiveAs,
  CoupaLocationLinkRequest,
  CoupaLocationLinkRow,
  CoupaOrderRow,
  CoupaPunchoutCart,
  CoupaPunchoutSiteOption,
  CoupaPunchoutStart,
  CoupaReasonCode,
  CoupaReceiveAs,
  CoupaReceiveRequest,
  CoupaReceiveResponse,
  CoupaReceiveWithoutStockRequest,
  CoupaReconciliation,
  CoupaRequisitionFields,
  CoupaRequisitionOptions,
  CoupaRequisitionRequest,
  CoupaRequisitionRow,
  CoupaShipToAddressRequest,
  CoupaShipToOption,
  CoupaShipToOptions,
  CoupaStaffLoginStatus,
  CoupaStaffSignInSaveRequest,
  CoupaStaffSignInStart,
  CoupaStockOptions,
  CoupaSupplierLinkRow,
  CoupaSyncRunResponse,
  CoupaSyncStatus,
  CoupaToReceive,
  CoupaWarehouseOption,
} from "@/types/coupa";
import { Stock } from "@/types/inventory/stock";

import { requestData } from "@/api/api-config";
import { stockQueryKeys } from "@/query/inventory/stock";
import { supplierQueryKeys } from "@/query/inventory/supplier";

// --- API Functions ---

async function getConnection(): Promise<CoupaConnectionStatus | null> {
  return await requestData<CoupaConnectionStatus>(
    "coupa/settings/connection",
    "get",
    undefined,
    false,
  );
}

async function saveConnection(
  data: CoupaConnectionSaveRequest,
): Promise<CoupaConnectionStatus | null> {
  return await requestData<CoupaConnectionStatus>(
    "coupa/settings/connection",
    "put",
    { json: data, timeout: 60000 },
    false,
  );
}

async function testConnection(): Promise<CoupaConnectionTestResult | null> {
  try {
    return await requestData<CoupaConnectionTestResult>(
      "coupa/settings/connection/test",
      "post",
      { timeout: 60000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't reach Coupa."));
  }
}

async function saveStaffSignIn(
  data: CoupaStaffSignInSaveRequest,
): Promise<CoupaConnectionStatus | null> {
  return await requestData<CoupaConnectionStatus>(
    "coupa/settings/connection/staff-sign-in",
    "put",
    { json: data },
    false,
  );
}

async function turnOffStaffSignIn(): Promise<CoupaConnectionStatus | null> {
  return await requestData<CoupaConnectionStatus>(
    "coupa/settings/connection/staff-sign-in",
    "delete",
    undefined,
    false,
  );
}

async function getMyCoupaLogin(): Promise<CoupaStaffLoginStatus | null> {
  return await requestData<CoupaStaffLoginStatus>(
    "coupa/me/login",
    "get",
    undefined,
    false,
  );
}

export async function startMyCoupaSignIn(): Promise<string | null> {
  const result = await requestData<CoupaStaffSignInStart>(
    "coupa/me/login/start",
    "post",
    undefined,
    false,
  );
  return result?.authorizationUrl ?? null;
}

async function signOutOfCoupa(): Promise<CoupaStaffLoginStatus | null> {
  return await requestData<CoupaStaffLoginStatus>(
    "coupa/me/login/sign-out",
    "post",
    undefined,
    false,
  );
}

async function getWarehouses(): Promise<CoupaWarehouseOption[] | null> {
  return await requestData<CoupaWarehouseOption[]>(
    "coupa/settings/warehouses",
    "get",
    { timeout: 60000 },
    false,
  );
}

async function getLocationLinks(): Promise<CoupaLocationLinkRow[] | null> {
  return await requestData<CoupaLocationLinkRow[]>(
    "coupa/settings/location-links",
    "get",
    undefined,
    false,
  );
}

async function putLocationLink({
  locationId,
  ...data
}: CoupaLocationLinkRequest & { locationId: string }): Promise<
  CoupaLocationLinkRow[] | null
> {
  return await requestData<CoupaLocationLinkRow[]>(
    `coupa/settings/location-links/${locationId}`,
    "put",
    { json: data, timeout: 60000 },
    false,
  );
}

async function deleteLocationLink(
  locationId: string,
): Promise<CoupaLocationLinkRow[] | null> {
  return await requestData<CoupaLocationLinkRow[]>(
    `coupa/settings/location-links/${locationId}`,
    "delete",
    undefined,
    false,
  );
}

async function getSupplierLinks(): Promise<CoupaSupplierLinkRow[] | null> {
  return await requestData<CoupaSupplierLinkRow[]>(
    "coupa/settings/supplier-links",
    "get",
    undefined,
    false,
  );
}

async function readHqSuppliersFromCoupa(): Promise<
  CoupaSupplierLinkRow[] | null
> {
  return await requestData<CoupaSupplierLinkRow[]>(
    "coupa/settings/supplier-links/refresh",
    "post",
    { timeout: 300000 },
    false,
  );
}

async function putSupplierLink({
  coupaSupplierId,
  supplierId,
}: {
  coupaSupplierId: number;
  supplierId: string;
}): Promise<CoupaSupplierLinkRow[] | null> {
  return await requestData<CoupaSupplierLinkRow[]>(
    `coupa/settings/supplier-links/${coupaSupplierId}`,
    "put",
    { json: { supplierId }, timeout: 60000 },
    false,
  );
}

async function deleteSupplierLink(
  coupaSupplierId: number,
): Promise<CoupaSupplierLinkRow[] | null> {
  return await requestData<CoupaSupplierLinkRow[]>(
    `coupa/settings/supplier-links/${coupaSupplierId}`,
    "delete",
    undefined,
    false,
  );
}

async function getItemLinks(): Promise<CoupaItemLinksResponse | null> {
  return await requestData<CoupaItemLinksResponse>(
    "coupa/settings/item-links",
    "get",
    { timeout: 60000 },
    false,
  );
}

async function saveItemLinks(
  data: CoupaItemLinksSaveRequest,
): Promise<CoupaItemLinksResponse | null> {
  return await requestData<CoupaItemLinksResponse>(
    "coupa/settings/item-links",
    "post",
    { json: data, timeout: 120000 },
    false,
  );
}

async function addToItemMaster(
  data: CoupaItemMasterAddRequest,
): Promise<CoupaItemLinkRow | null> {
  return await requestData<CoupaItemLinkRow>(
    "coupa/settings/item-master",
    "post",
    { json: data, timeout: 60000 },
    false,
  );
}

async function getCommodities(): Promise<CoupaCommoditiesResponse | null> {
  return await requestData<CoupaCommoditiesResponse>(
    "coupa/settings/commodities",
    "get",
    { timeout: 60000 },
    false,
  );
}

async function putCommodityRule({
  commodityId,
  name,
  receiveAs,
}: {
  commodityId: number;
  name: string;
  receiveAs: CoupaReceiveAs | null;
}): Promise<CoupaCommodityRuleResponse | null> {
  return await requestData<CoupaCommodityRuleResponse>(
    `coupa/settings/commodities/${commodityId}`,
    "put",
    { json: { name, receiveAs } },
    false,
  );
}

async function getItemRules(): Promise<CoupaItemRulesResponse | null> {
  return await requestData<CoupaItemRulesResponse>(
    "coupa/settings/item-rules",
    "get",
    { timeout: 60000 },
    false,
  );
}

async function putItemRule({
  coupaItemId,
  name,
  itemNumber,
  receiveAs,
}: {
  coupaItemId: number;
  name: string;
  itemNumber: string | null;
  receiveAs: CoupaReceiveAs | null;
}): Promise<CoupaItemRuleResponse | null> {
  return await requestData<CoupaItemRuleResponse>(
    `coupa/settings/item-rules/${coupaItemId}`,
    "put",
    { json: { name, itemNumber, receiveAs } },
    false,
  );
}

async function deleteItemLink(
  stockId: string,
): Promise<CoupaItemLinksResponse | null> {
  return await requestData<CoupaItemLinksResponse>(
    `coupa/settings/item-links/${stockId}`,
    "delete",
    { timeout: 60000 },
    false,
  );
}

async function getSyncStatus(): Promise<CoupaSyncStatus | null> {
  return await requestData<CoupaSyncStatus>(
    "coupa/sync/status",
    "get",
    undefined,
    false,
  );
}

async function runSync(): Promise<CoupaSyncRunResponse | null> {
  return await requestData<CoupaSyncRunResponse>(
    "coupa/sync/run",
    "post",
    { timeout: 120000 },
    false,
  );
}

async function retryPosting(
  postingId: string,
): Promise<CoupaSyncStatus | null> {
  return await requestData<CoupaSyncStatus>(
    `coupa/sync/postings/${postingId}/retry`,
    "post",
    undefined,
    false,
  );
}

async function retryAllFailed(): Promise<CoupaSyncStatus | null> {
  return await requestData<CoupaSyncStatus>(
    "coupa/sync/postings/retry-failed",
    "post",
    undefined,
    false,
  );
}

async function applyEarliestLot(
  postingId: string,
): Promise<CoupaSyncStatus | null> {
  return await requestData<CoupaSyncStatus>(
    `coupa/sync/postings/${postingId}/earliest-lot`,
    "post",
    undefined,
    false,
  );
}

async function dismissPosting(
  postingId: string,
): Promise<CoupaSyncStatus | null> {
  return await requestData<CoupaSyncStatus>(
    `coupa/sync/postings/${postingId}/dismiss`,
    "post",
    undefined,
    false,
  );
}

async function applyInboundRow(rowId: string): Promise<CoupaSyncStatus | null> {
  return await requestData<CoupaSyncStatus>(
    `coupa/sync/inbound/${rowId}/apply`,
    "post",
    undefined,
    false,
  );
}

async function dismissInboundRow(
  rowId: string,
): Promise<CoupaSyncStatus | null> {
  return await requestData<CoupaSyncStatus>(
    `coupa/sync/inbound/${rowId}/dismiss`,
    "post",
    undefined,
    false,
  );
}

async function getReconciliation(): Promise<CoupaReconciliation | null> {
  return await requestData<CoupaReconciliation>(
    "coupa/reconciliation",
    "get",
    undefined,
    false,
  );
}

async function refreshReconciliation(): Promise<CoupaReconciliation | null> {
  return await requestData<CoupaReconciliation>(
    "coupa/reconciliation/refresh",
    "post",
    { timeout: 120000 },
    false,
  );
}

async function fixReconciliation(
  data: CoupaFixDriftRequest,
): Promise<CoupaReconciliation | null> {
  try {
    return await requestData<CoupaReconciliation>(
      "coupa/reconciliation/fix",
      "post",
      { json: data, timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't fix this drift."));
  }
}

async function getGoLivePreview(
  locationId: string,
): Promise<CoupaGoLivePreview | null> {
  return await requestData<CoupaGoLivePreview>(
    `coupa/go-live/${locationId}/preview`,
    "get",
    { timeout: 60000 },
    false,
  );
}

async function getRequisitionOptions(): Promise<CoupaRequisitionOptions | null> {
  return await requestData<CoupaRequisitionOptions>(
    "coupa/requisitions/options",
    "get",
    undefined,
    false,
  );
}

async function getShipTo(
  locationId: string,
): Promise<CoupaShipToOptions | null> {
  try {
    return await requestData<CoupaShipToOptions>(
      `coupa/requisitions/ship-to?location_id=${encodeURIComponent(locationId)}`,
      "get",
      { timeout: 60000 },
      false,
    );
  } catch (error) {
    throw new Error(
      await parseError(error, "Couldn't load delivery addresses."),
    );
  }
}

async function addShipTo(
  data: CoupaShipToAddressRequest,
): Promise<CoupaShipToOption | null> {
  try {
    return await requestData<CoupaShipToOption>(
      "coupa/requisitions/ship-to",
      "post",
      { json: data, timeout: 60000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't add the address."));
  }
}

async function createRequisition(
  data: CoupaRequisitionRequest,
): Promise<CoupaRequisitionRow | null> {
  try {
    return await requestData<CoupaRequisitionRow>(
      "coupa/requisitions",
      "post",
      { json: data, timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't send this to Coupa."));
  }
}

async function getPunchoutSites(): Promise<CoupaPunchoutSiteOption[] | null> {
  try {
    return await requestData<CoupaPunchoutSiteOption[]>(
      "coupa/punchout/sites",
      "get",
      { timeout: 60000 },
      false,
    );
  } catch (error) {
    throw new Error(
      await parseError(error, "Couldn't load suppliers from Coupa."),
    );
  }
}

async function startPunchout(
  siteId: number,
): Promise<CoupaPunchoutStart | null> {
  try {
    return await requestData<CoupaPunchoutStart>(
      "coupa/punchouts",
      "post",
      { json: { siteId }, timeout: 60000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't open that supplier."));
  }
}

async function getPunchoutCart(
  punchoutId: string,
): Promise<CoupaPunchoutCart | null> {
  try {
    return await requestData<CoupaPunchoutCart>(
      `coupa/punchouts/${punchoutId}`,
      "get",
      undefined,
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't load that cart."));
  }
}

async function retryRequisition(
  requisitionId: string,
): Promise<CoupaRequisitionRow | null> {
  try {
    return await requestData<CoupaRequisitionRow>(
      `coupa/requisitions/${requisitionId}/retry`,
      "post",
      { timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't send this to Coupa."));
  }
}

interface CoupaRequisitionResend extends CoupaRequisitionFields {
  requisitionId: string;
}

async function resendRequisition({
  requisitionId,
  ...data
}: CoupaRequisitionResend): Promise<CoupaRequisitionRow | null> {
  try {
    return await requestData<CoupaRequisitionRow>(
      `coupa/requisitions/${requisitionId}`,
      "put",
      { json: data, timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't send this to Coupa."));
  }
}

async function removeRequisition(requisitionId: string): Promise<void> {
  try {
    await requestData<null>(
      `coupa/requisitions/${requisitionId}`,
      "delete",
      undefined,
      false,
    );
  } catch (error) {
    throw new Error(
      await parseError(error, "Couldn't remove this requisition."),
    );
  }
}

interface CoupaGoLiveVariables {
  locationId: string;
  source: CoupaGoLiveChoice;
  acknowledged: CoupaGoLiveWarning[];
}

async function goLive({
  locationId,
  ...data
}: CoupaGoLiveVariables): Promise<CoupaLocationLinkRow[] | null> {
  return await requestData<CoupaLocationLinkRow[]>(
    `coupa/go-live/${locationId}`,
    "post",
    { json: data, timeout: 120000 },
    false,
  );
}

async function getLiveLocations(): Promise<string[] | null> {
  return await requestData<string[]>(
    "coupa/live-locations",
    "get",
    undefined,
    false,
  );
}

async function getOrders(open: boolean): Promise<CoupaOrderRow[] | null> {
  return await requestData<CoupaOrderRow[]>(
    `coupa/orders?open=${open}`,
    "get",
    undefined,
    false,
  );
}

async function getOrdersToReceive(
  locationIds: string[] | undefined,
): Promise<CoupaToReceive | null> {
  const query = new URLSearchParams(
    (locationIds ?? []).map((id) => ["location_ids", id]),
  ).toString();
  return await requestData<CoupaToReceive>(
    `coupa/orders/to-receive${query ? `?${query}` : ""}`,
    "get",
    undefined,
    false,
  );
}

async function getRequisitions(): Promise<CoupaRequisitionRow[] | null> {
  return await requestData<CoupaRequisitionRow[]>(
    "coupa/requisitions",
    "get",
    undefined,
    false,
  );
}

async function getApprovals(): Promise<CoupaApprovalRow[] | null> {
  return await requestData<CoupaApprovalRow[]>(
    "coupa/approvals",
    "get",
    { timeout: 120000 },
    false,
  );
}

async function outcomeUnknown(error: unknown): Promise<boolean> {
  if (
    error instanceof TypeError ||
    (error as Error | null)?.name === "TimeoutError"
  ) {
    return true;
  }
  const status = (error as { response?: Response } | null)?.response?.status;
  return (
    status !== undefined &&
    status >= 500 &&
    (await readErrorDetail(error)) === undefined
  );
}

async function actOnApproval(
  verb: "approve" | "reject",
  { requisitionId, approvalId, reason }: CoupaApprovalAction,
): Promise<void> {
  try {
    await requestData<null>(
      `coupa/approvals/${requisitionId}/${verb}`,
      "post",
      { json: { approvalId, reason }, timeout: 150000 },
      false,
    );
  } catch (error) {
    if (await outcomeUnknown(error)) {
      const action = verb === "approve" ? "approval" : "rejection";
      throw new Error(
        `Coupa didn't confirm the ${action}, so it may have gone through. Refresh in a minute to check.`,
      );
    }
    throw new Error(await parseError(error, `Couldn't ${verb} this in Coupa.`));
  }
}

async function getOrder(coupaOrderId: number): Promise<CoupaOrderRow[] | null> {
  return await requestData<CoupaOrderRow[]>(
    `coupa/orders?open=false&coupa_order_id=${coupaOrderId}`,
    "get",
    undefined,
    false,
  );
}

async function receiveOrderLine({
  lineId,
  ...data
}: CoupaReceiveRequest & {
  lineId: string;
}): Promise<CoupaReceiveResponse | null> {
  try {
    return await requestData<CoupaReceiveResponse>(
      `coupa/orders/lines/${lineId}/receive`,
      "post",
      { json: data, timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't receive this."));
  }
}

async function receiveWithoutStock({
  lineId,
  ...data
}: CoupaReceiveWithoutStockRequest & {
  lineId: string;
}): Promise<CoupaReceiveResponse | null> {
  try {
    return await requestData<CoupaReceiveResponse>(
      `coupa/orders/lines/${lineId}/receive-without-stock`,
      "post",
      { json: data, timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't receive this."));
  }
}

async function retryWithoutStock(
  lineId: string,
): Promise<CoupaReceiveResponse | null> {
  try {
    return await requestData<CoupaReceiveResponse>(
      `coupa/orders/lines/${lineId}/receive-without-stock/retry`,
      "post",
      { timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't check with Coupa."));
  }
}

async function getLineLinkOptions(
  lineId: string,
): Promise<CoupaLineLinkOptions | null> {
  try {
    return await requestData<CoupaLineLinkOptions>(
      `coupa/orders/lines/${lineId}/link-options`,
      "get",
      { timeout: 60000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't read this Coupa item."));
  }
}

async function getItemPrice(stockId: string): Promise<CoupaItemPrice | null> {
  try {
    return await requestData<CoupaItemPrice>(
      `coupa/requisitions/items/${stockId}/price`,
      "get",
      { timeout: 30000 },
      false,
    );
  } catch (error) {
    throw new Error(
      await parseError(error, "Couldn't check the price in Coupa just now."),
    );
  }
}

async function linkOrderLine({
  lineId,
  ...data
}: CoupaLineLinkRequest & {
  lineId: string;
}): Promise<CoupaLineLinkResponse | null> {
  try {
    return await requestData<CoupaLineLinkResponse>(
      `coupa/orders/lines/${lineId}/link`,
      "post",
      { json: data, timeout: 120000 },
      false,
    );
  } catch (error) {
    throw new Error(await parseError(error, "Couldn't link this."));
  }
}

async function getReasonCodes(): Promise<CoupaReasonCode[] | null> {
  return await requestData<CoupaReasonCode[]>(
    "coupa/settings/reason-codes",
    "get",
    undefined,
    false,
  );
}

async function saveReasonCodes(
  codes: Record<string, string | null>,
): Promise<CoupaReasonCode[] | null> {
  return await requestData<CoupaReasonCode[]>(
    "coupa/settings/reason-codes",
    "put",
    { json: { codes } },
    false,
  );
}

async function saveStockOptions(
  options: Partial<CoupaStockOptions>,
): Promise<CoupaStockOptions | null> {
  return await requestData<CoupaStockOptions>(
    "coupa/settings/stock-options",
    "put",
    { json: options },
    false,
  );
}

// --- Query Keys ---

export const coupaQueryKeys = {
  all: ["coupa"] as const,
  connection: () => [...coupaQueryKeys.all, "connection"] as const,
  myLogin: () => [...coupaQueryKeys.all, "my-login"] as const,
  warehouses: () => [...coupaQueryKeys.all, "warehouses"] as const,
  locationLinks: () => [...coupaQueryKeys.all, "location-links"] as const,
  itemLinks: () => [...coupaQueryKeys.all, "item-links"] as const,
  commodities: () => [...coupaQueryKeys.all, "commodities"] as const,
  itemRules: () => [...coupaQueryKeys.all, "item-rules"] as const,
  supplierLinks: () => [...coupaQueryKeys.all, "supplier-links"] as const,
  syncStatus: () => [...coupaQueryKeys.all, "sync-status"] as const,
  liveLocations: () => [...coupaQueryKeys.all, "live-locations"] as const,
  reasonCodes: () => [...coupaQueryKeys.all, "reason-codes"] as const,
  reconciliation: () => [...coupaQueryKeys.all, "reconciliation"] as const,
  orders: (open: boolean) => [...coupaQueryKeys.all, "orders", open] as const,
  order: (coupaOrderId: number) =>
    [...coupaQueryKeys.all, "orders", "one", coupaOrderId] as const,
  toReceive: (locationIds?: string[]) =>
    [...coupaQueryKeys.all, "to-receive", locationIds ?? null] as const,
  goLivePreview: (locationId: string) =>
    [...coupaQueryKeys.all, "go-live-preview", locationId] as const,
  lineLinkOptions: (lineId: string) =>
    [...coupaQueryKeys.all, "line-link-options", lineId] as const,
  requisitionOptions: () =>
    [...coupaQueryKeys.all, "requisition-options"] as const,
  shipTo: (locationId: string) =>
    [...coupaQueryKeys.all, "ship-to", locationId] as const,
  requisitions: () => [...coupaQueryKeys.all, "requisitions"] as const,
  itemPrice: (stockId: string) =>
    [...coupaQueryKeys.all, "item-price", stockId] as const,
  punchoutSites: () => [...coupaQueryKeys.all, "punchout-sites"] as const,
  punchoutCart: (punchoutId: string) =>
    [...coupaQueryKeys.all, "punchout-cart", punchoutId] as const,
  approvals: () => [...coupaQueryKeys.all, "approvals"] as const,
};

// --- Query Options ---

const NEXT_DAY_RECHECK_FLOOR = 60 * 1000;

const refetchAtNextClinicDay = (query: {
  state: { data?: CoupaOrderRow[] | null };
}) => {
  const dayEnds = (query.state.data ?? [])
    .filter((order) => order.isOpen)
    .map((order) => Date.parse(order.clinicDayEndsAt));
  return dayEnds.length === 0
    ? false
    : Math.max(Math.min(...dayEnds) - Date.now(), NEXT_DAY_RECHECK_FLOOR);
};

export const coupaQueryOptions = {
  connection: () => ({
    queryKey: coupaQueryKeys.connection(),
    queryFn: getConnection,
  }),
  myLogin: () => ({
    queryKey: coupaQueryKeys.myLogin(),
    queryFn: getMyCoupaLogin,
  }),
  warehouses: () => ({
    queryKey: coupaQueryKeys.warehouses(),
    queryFn: getWarehouses,
    staleTime: 5 * 60 * 1000,
  }),
  locationLinks: () => ({
    queryKey: coupaQueryKeys.locationLinks(),
    queryFn: getLocationLinks,
  }),
  itemLinks: () => ({
    queryKey: coupaQueryKeys.itemLinks(),
    queryFn: async () =>
      keepCoupaData(
        await getItemLinks(),
        queryClient.getQueryData<CoupaItemLinksResponse | null>(
          coupaQueryKeys.itemLinks(),
        ),
      ),
  }),
  commodities: () => ({
    queryKey: coupaQueryKeys.commodities(),
    queryFn: getCommodities,
  }),
  itemRules: () => ({
    queryKey: coupaQueryKeys.itemRules(),
    queryFn: getItemRules,
  }),
  supplierLinks: () => ({
    queryKey: coupaQueryKeys.supplierLinks(),
    queryFn: getSupplierLinks,
  }),
  syncStatus: () => ({
    queryKey: coupaQueryKeys.syncStatus(),
    queryFn: getSyncStatus,
    staleTime: 0,
  }),
  liveLocations: () => ({
    queryKey: coupaQueryKeys.liveLocations(),
    queryFn: getLiveLocations,
    staleTime: 5 * 60 * 1000,
  }),
  reasonCodes: () => ({
    queryKey: coupaQueryKeys.reasonCodes(),
    queryFn: getReasonCodes,
  }),
  reconciliation: () => ({
    queryKey: coupaQueryKeys.reconciliation(),
    queryFn: getReconciliation,
  }),
  orders: (open: boolean) => ({
    queryKey: coupaQueryKeys.orders(open),
    queryFn: () => getOrders(open),
    refetchInterval: refetchAtNextClinicDay,
  }),
  order: (coupaOrderId: number) => ({
    queryKey: coupaQueryKeys.order(coupaOrderId),
    queryFn: () => getOrder(coupaOrderId),
    refetchInterval: refetchAtNextClinicDay,
  }),
  toReceive: (locationIds?: string[]) => ({
    queryKey: coupaQueryKeys.toReceive(locationIds),
    queryFn: () => getOrdersToReceive(locationIds),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  }),
  lineLinkOptions: (lineId: string) => ({
    queryKey: coupaQueryKeys.lineLinkOptions(lineId),
    queryFn: () => getLineLinkOptions(lineId),
    staleTime: 0,
    retry: false,
  }),
  itemPrice: (stockId: string) => ({
    queryKey: coupaQueryKeys.itemPrice(stockId),
    queryFn: () => getItemPrice(stockId),
    staleTime: 60 * 1000,
    retry: false,
  }),
  requisitions: () => ({
    queryKey: coupaQueryKeys.requisitions(),
    queryFn: getRequisitions,
    refetchInterval: 60 * 1000,
  }),
  punchoutSites: () => ({
    queryKey: coupaQueryKeys.punchoutSites(),
    queryFn: getPunchoutSites,
    retry: false,
  }),
  punchoutCart: (punchoutId: string) => ({
    queryKey: coupaQueryKeys.punchoutCart(punchoutId),
    queryFn: () => getPunchoutCart(punchoutId),
    staleTime: Infinity,
    retry: false,
  }),
  requisitionOptions: () => ({
    queryKey: coupaQueryKeys.requisitionOptions(),
    queryFn: getRequisitionOptions,
    staleTime: 0,
  }),
  shipTo: (locationId: string) => ({
    queryKey: coupaQueryKeys.shipTo(locationId),
    queryFn: () => getShipTo(locationId),
    enabled: Boolean(locationId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  }),
  goLivePreview: (locationId: string) => ({
    queryKey: coupaQueryKeys.goLivePreview(locationId),
    queryFn: async () => {
      try {
        return await getGoLivePreview(locationId);
      } catch (error) {
        throw new Error(
          await parseError(error, "Couldn't read Coupa's stock here."),
        );
      }
    },
    gcTime: 0,
  }),
  approvals: () => ({
    queryKey: coupaQueryKeys.approvals(),
    queryFn: getApprovals,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  }),
};

// --- Cache helpers ---

function patchLinkCounts(
  counts: Partial<
    Pick<CoupaConnectionStatus, "linkedLocationCount" | "linkedItemCount">
  >,
): void {
  queryClient.setQueryData<CoupaConnectionStatus | null>(
    coupaQueryKeys.connection(),
    (previous) => (previous ? { ...previous, ...counts } : previous),
  );
}

function storeLocationLinks(rows: CoupaLocationLinkRow[] | null): void {
  if (!rows) return;
  queryClient.setQueryData(coupaQueryKeys.locationLinks(), rows);
  queryClient.setQueryData(
    coupaQueryKeys.liveLocations(),
    rows.filter((row) => row.wentLiveAt).map((row) => row.locationId),
  );
  patchLinkCounts({
    linkedLocationCount: rows.filter((row) => row.coupaWarehouseLocationId)
      .length,
  });
}

function keepCoupaData(
  response: CoupaItemLinksResponse | null,
  previous: CoupaItemLinksResponse | null | undefined,
): CoupaItemLinksResponse | null {
  if (!response || response.coupaReachable || !previous) return response;
  return {
    ...response,
    items: previous.items,
    rows: response.rows.map((row) => ({
      ...row,
      suggestion: row.link
        ? null
        : (previous.rows.find((old) => old.stockId === row.stockId)
            ?.suggestion ?? null),
    })),
  };
}

function storeItemLinks(response: CoupaItemLinksResponse | null): void {
  if (!response) return;
  queryClient.setQueryData<CoupaItemLinksResponse | null>(
    coupaQueryKeys.itemLinks(),
    (previous) => keepCoupaData(response, previous),
  );
  patchLinkCounts({
    linkedItemCount: response.rows.filter((row) => row.link).length,
  });
  queryClient.removeQueries({
    queryKey: [...coupaQueryKeys.all, "item-price"],
  });
}

function storeSupplierLinks(rows: CoupaSupplierLinkRow[] | null): void {
  if (!rows) return;
  queryClient.setQueryData(coupaQueryKeys.supplierLinks(), rows);
}

async function showError(error: unknown, fallback: string): Promise<void> {
  toast({
    title: "Error",
    description:
      error instanceof Error && error.constructor === Error && error.message
        ? error.message
        : await parseError(error, fallback),
    variant: "error",
  });
}

// --- Mutations ---

export const useSaveCoupaConnectionMutation = () => {
  return useMutation<
    CoupaConnectionStatus | null,
    Error,
    CoupaConnectionSaveRequest
  >({
    mutationFn: saveConnection,
    onSuccess: (data) => {
      const previous = queryClient.getQueryData<CoupaConnectionStatus | null>(
        coupaQueryKeys.connection(),
      );
      queryClient.setQueryData(coupaQueryKeys.connection(), data);
      // Coupa ids differ between instances, so anything read from the old host is stale.
      if (previous?.host !== data?.host) {
        queryClient.removeQueries({
          queryKey: coupaQueryKeys.all,
          predicate: (query) => query.queryKey[1] !== "connection",
        });
      }
    },
    onError: (error) =>
      showError(
        error,
        "Couldn't connect to Coupa. Check the address, client ID and secret.",
      ),
  });
};

const storeConnection = (data: CoupaConnectionStatus | null) => {
  if (data) queryClient.setQueryData(coupaQueryKeys.connection(), data);
};

export const useSaveCoupaStaffSignInMutation = () => {
  return useMutation<
    CoupaConnectionStatus | null,
    Error,
    CoupaStaffSignInSaveRequest
  >({
    mutationFn: saveStaffSignIn,
    onSuccess: storeConnection,
    onError: (error) =>
      showError(error, "Couldn't save staff sign-in. Please try again."),
  });
};

export const useTurnOffCoupaStaffSignInMutation = () => {
  return useMutation<CoupaConnectionStatus | null, Error, void>({
    mutationFn: turnOffStaffSignIn,
    onSuccess: storeConnection,
    onError: (error) =>
      showError(error, "Couldn't turn off staff sign-in. Please try again."),
  });
};

/** A finished sign-in changed the person's Coupa login on the server, so it has to be read again. */
export function refreshMyCoupaLogin() {
  return queryClient.invalidateQueries({ queryKey: coupaQueryKeys.myLogin() });
}

export const useSignOutOfCoupaMutation = () => {
  return useMutation<CoupaStaffLoginStatus | null, Error, void>({
    mutationFn: signOutOfCoupa,
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(coupaQueryKeys.myLogin(), data);
      else void refreshMyCoupaLogin();
    },
    onError: (error) =>
      showError(error, "Couldn't sign out of Coupa. Please try again."),
  });
};

export const useTestCoupaConnectionMutation = () => {
  return useMutation<CoupaConnectionTestResult | null, Error, void>({
    mutationFn: testConnection,
  });
};

export const useLinkCoupaLocationMutation = () => {
  return useMutation<
    CoupaLocationLinkRow[] | null,
    Error,
    CoupaLocationLinkRequest & { locationId: string }
  >({
    mutationFn: putLocationLink,
    onSuccess: storeLocationLinks,
    onError: (error) =>
      showError(error, "Couldn't link that location to Coupa."),
  });
};

export const useUnlinkCoupaLocationMutation = () => {
  return useMutation<CoupaLocationLinkRow[] | null, Error, string>({
    mutationFn: deleteLocationLink,
    onSuccess: storeLocationLinks,
    onError: (error) => showError(error, "Couldn't unlink that location."),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
      queryClient.invalidateQueries({
        queryKey: coupaQueryKeys.reconciliation(),
      });
      refreshCoupaOrders();
    },
  });
};

export const useMatchCoupaSupplierMutation = () => {
  return useMutation<
    CoupaSupplierLinkRow[] | null,
    Error,
    { coupaSupplierId: number; supplierId: string }
  >({
    mutationFn: putSupplierLink,
    onSuccess: (rows) => {
      storeSupplierLinks(rows);
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: supplierQueryKeys.all });
    },
    onError: (error) => showError(error, "Couldn't match that supplier."),
  });
};

export const useReadHqSuppliersFromCoupaMutation = () => {
  return useMutation<CoupaSupplierLinkRow[] | null, Error, void>({
    mutationFn: readHqSuppliersFromCoupa,
    onSuccess: storeSupplierLinks,
    onError: (error) =>
      showError(error, "Couldn't read the suppliers from Coupa."),
  });
};

export const useUnmatchCoupaSupplierMutation = () => {
  return useMutation<CoupaSupplierLinkRow[] | null, Error, number>({
    mutationFn: deleteSupplierLink,
    onSuccess: (rows) => {
      storeSupplierLinks(rows);
      queryClient.invalidateQueries({ queryKey: supplierQueryKeys.all });
    },
    onError: (error) => showError(error, "Couldn't clear that match."),
  });
};

export const useSaveCoupaItemLinksMutation = () => {
  return useMutation<
    CoupaItemLinksResponse | null,
    Error,
    CoupaItemLinksSaveRequest
  >({
    mutationFn: saveItemLinks,
    onSuccess: (response) => {
      storeItemLinks(response);
      if (response?.comparison.length) return;
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
    },
    onError: (error) => showError(error, "Couldn't link those stock items."),
  });
};

export const useAddToCoupaItemMasterMutation = () => {
  return useMutation<CoupaItemLinkRow | null, Error, CoupaItemMasterAddRequest>(
    {
      mutationFn: addToItemMaster,
      onSuccess: (row) => {
        if (!row) return;
        const response =
          queryClient.setQueryData<CoupaItemLinksResponse | null>(
            coupaQueryKeys.itemLinks(),
            (previous) =>
              previous && {
                ...previous,
                rows: [...previous.rows, row].sort((a, b) =>
                  a.stockName.localeCompare(b.stockName),
                ),
              },
          );
        if (response) {
          patchLinkCounts({
            linkedItemCount: response.rows.filter((each) => each.link).length,
          });
        }
        queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
      },
      onError: (error) =>
        showError(error, "Couldn't add that Coupa item to the item master."),
    },
  );
};

export const useUnlinkCoupaItemMutation = () => {
  return useMutation<CoupaItemLinksResponse | null, Error, string>({
    mutationFn: deleteItemLink,
    onSuccess: storeItemLinks,
    onError: (error) => showError(error, "Couldn't unlink that stock item."),
  });
};

function storeLineReceiveAs(decided: CoupaLineReceiveAs[]): void {
  const byId = new Map(decided.map((line) => [line.id, line]));
  queryClient.setQueriesData<CoupaOrderRow[] | null>(
    { queryKey: [...coupaQueryKeys.all, "orders"] },
    (orders) =>
      orders?.map((order) => ({
        ...order,
        lines: order.lines.map((line) => {
          const decision = byId.get(line.id);
          return decision
            ? {
                ...line,
                receiveAs: decision.receiveAs,
                receiveAsCommodity: decision.receiveAsCommodity,
                receiveAsByItem: decision.receiveAsByItem,
              }
            : line;
        }),
      })) ?? orders,
  );
}

export const useSetCoupaCommodityRuleMutation = () => {
  return useMutation<
    CoupaCommodityRuleResponse | null,
    Error,
    { commodityId: number; name: string; receiveAs: CoupaReceiveAs | null }
  >({
    mutationFn: putCommodityRule,
    onSuccess: (response) => {
      if (!response) return;
      const { commodityId, receiveAs } = response.commodity;
      queryClient.setQueryData<CoupaCommoditiesResponse | null>(
        coupaQueryKeys.commodities(),
        (previous) =>
          previous && {
            ...previous,
            rows: previous.rows.map((row) =>
              row.commodityId === commodityId ? { ...row, receiveAs } : row,
            ),
          },
      );
      queryClient.setQueryData<CoupaItemRulesResponse | null>(
        coupaQueryKeys.itemRules(),
        (previous) =>
          previous && {
            ...previous,
            commodities: previous.commodities.map((row) =>
              row.commodityId === commodityId ? { ...row, receiveAs } : row,
            ),
          },
      );
      storeLineReceiveAs(response.lines);
    },
    onError: (error) =>
      showError(error, "Couldn't change how that commodity is received."),
  });
};

export const useSetCoupaItemRuleMutation = () => {
  return useMutation<
    CoupaItemRuleResponse | null,
    Error,
    {
      coupaItemId: number;
      name: string;
      itemNumber: string | null;
      receiveAs: CoupaReceiveAs | null;
    }
  >({
    mutationFn: putItemRule,
    onSuccess: (response) => {
      if (!response) return;
      const { coupaItemId, receiveAs } = response.item;
      queryClient.setQueryData<CoupaItemRulesResponse | null>(
        coupaQueryKeys.itemRules(),
        (previous) =>
          previous && {
            ...previous,
            rows: previous.rows.map((row) =>
              row.coupaItemId === coupaItemId ? { ...row, receiveAs } : row,
            ),
          },
      );
      storeLineReceiveAs(response.lines);
    },
    onError: (error) =>
      showError(error, "Couldn't change how that item is received."),
  });
};

function storeSyncStatus(status: CoupaSyncStatus | null): void {
  if (status) queryClient.setQueryData(coupaQueryKeys.syncStatus(), status);
}

function refreshCoupaSuppliers(): void {
  queryClient.invalidateQueries({ queryKey: coupaQueryKeys.supplierLinks() });
  queryClient.invalidateQueries({ queryKey: supplierQueryKeys.all });
}

function storeInboundReview(status: CoupaSyncStatus | null): void {
  storeSyncStatus(status);
  queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
}

export const useRunCoupaSyncMutation = () => {
  return useMutation<CoupaSyncRunResponse | null, Error, void>({
    mutationFn: runSync,
    onSuccess: (data) => {
      storeSyncStatus(data?.status ?? null);
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: coupaQueryKeys.requisitions(),
      });
      refreshCoupaOrders();
      refreshCoupaSuppliers();
    },
    onError: (error) => showError(error, "Couldn't sync with Coupa."),
  });
};

export const useRetryCoupaPostingMutation = () => {
  return useMutation<CoupaSyncStatus | null, Error, string>({
    mutationFn: retryPosting,
    onSuccess: storeSyncStatus,
    onError: (error) => showError(error, "Couldn't queue that again."),
  });
};

export const useEarliestCoupaLotMutation = () => {
  return useMutation<CoupaSyncStatus | null, Error, string>({
    mutationFn: applyEarliestLot,
    onSuccess: storeSyncStatus,
    onError: (error) => showError(error, "Couldn't use Coupa's lot."),
  });
};

export const useRetryAllFailedCoupaPostingsMutation = () => {
  return useMutation<CoupaSyncStatus | null, Error, void>({
    mutationFn: retryAllFailed,
    onSuccess: storeSyncStatus,
    onError: (error) => showError(error, "Couldn't queue those again."),
  });
};

const TO_RECEIVE_KEY = [...coupaQueryKeys.all, "to-receive"] as const;

const isToReceive = (order: CoupaOrderRow) =>
  order.isOpen && !order.placedToday;

function storeToReceive(before: CoupaOrderRow, after: CoupaOrderRow): void {
  if (!isToReceive(before) || isToReceive(after)) return;
  const queries = queryClient.getQueriesData<CoupaToReceive | null>({
    queryKey: TO_RECEIVE_KEY,
  });
  for (const [key] of queries) {
    const locationIds = key[TO_RECEIVE_KEY.length] as string[] | null;
    if (locationIds && !locationIds.includes(after.locationId)) continue;
    queryClient.setQueryData<CoupaToReceive | null>(
      key,
      (counts) => counts && { count: Math.max(counts.count - 1, 0) },
    );
  }
}

function storeCoupaOrder(order: CoupaOrderRow): void {
  const isSameOrder = (row: CoupaOrderRow) =>
    row.coupaOrderId === order.coupaOrderId &&
    row.locationId === order.locationId;
  const before = [
    coupaQueryKeys.orders(false),
    coupaQueryKeys.orders(true),
    coupaQueryKeys.order(order.coupaOrderId),
  ]
    .flatMap(
      (key) => queryClient.getQueryData<CoupaOrderRow[] | null>(key) ?? [],
    )
    .find(isSameOrder);
  if (before) storeToReceive(before, order);
  const replace = (rows: CoupaOrderRow[] | null | undefined, open: boolean) =>
    rows?.flatMap((row) =>
      isSameOrder(row) ? (open && !order.isOpen ? [] : [order]) : [row],
    );
  for (const open of [true, false]) {
    queryClient.setQueryData<CoupaOrderRow[] | null>(
      coupaQueryKeys.orders(open),
      (rows) => replace(rows, open) ?? rows,
    );
  }
  queryClient.setQueryData<CoupaOrderRow[] | null>(
    coupaQueryKeys.order(order.coupaOrderId),
    (rows) => replace(rows, false) ?? rows,
  );
}

function refreshCoupaOrders(): void {
  queryClient.invalidateQueries({
    queryKey: [...coupaQueryKeys.all, "orders"],
  });
  queryClient.invalidateQueries({ queryKey: TO_RECEIVE_KEY });
}

export const useReceiveCoupaOrderLineMutation = () => {
  return useMutation<
    CoupaReceiveResponse | null,
    Error,
    CoupaReceiveRequest & { lineId: string }
  >({
    mutationFn: receiveOrderLine,
    onSuccess: (response) => {
      if (response?.order) storeCoupaOrder(response.order);
      else refreshCoupaOrders();
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
      refreshCoupaSuppliers();
    },
    onError: (error) => {
      refreshCoupaOrders();
      showError(error, "Couldn't receive that line.");
    },
  });
};

function storeReceiptWithoutStock(response: CoupaReceiveResponse | null): void {
  if (response?.order) storeCoupaOrder(response.order);
  else refreshCoupaOrders();
}

export const useReceiveCoupaWithoutStockMutation = () => {
  return useMutation<
    CoupaReceiveResponse | null,
    Error,
    CoupaReceiveWithoutStockRequest & { lineId: string }
  >({
    mutationFn: receiveWithoutStock,
    onSuccess: storeReceiptWithoutStock,
    onError: (error) => {
      refreshCoupaOrders();
      showError(error, "Couldn't receive that line.");
    },
  });
};

function receiveDeliveryLine(
  line: CoupaDeliveryLine,
): Promise<CoupaReceiveResponse | null> {
  const { lineId, quantity, lots } = line;
  if (line.receiveAs === "STOCK") {
    return receiveOrderLine({ lineId, quantity, lots });
  }
  return receiveWithoutStock({
    lineId,
    quantity,
    lots,
    action: "EXPENSE",
    receivedOn: line.receivedOn,
  });
}

async function receiveDelivery(
  lines: CoupaDeliveryLine[],
): Promise<CoupaDeliveryResult[]> {
  const results: CoupaDeliveryResult[] = [];
  let stocked = false;
  let refresh = false;
  for (const line of lines) {
    try {
      const response = await receiveDeliveryLine(line);
      if (response?.order) storeCoupaOrder(response.order);
      else refresh = true;
      stocked ||= line.receiveAs === "STOCK";
      results.push({
        lineId: line.lineId,
        sent: response?.sent ?? false,
        error: null,
      });
    } catch (error) {
      refresh = true;
      results.push({
        lineId: line.lineId,
        sent: false,
        error:
          error instanceof Error && error.message
            ? error.message
            : "Couldn't receive this.",
      });
    }
  }
  if (stocked) {
    queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
    queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
    refreshCoupaSuppliers();
  }
  if (refresh) refreshCoupaOrders();
  return results;
}

export const useReceiveCoupaDeliveryMutation = () => {
  return useMutation<CoupaDeliveryResult[], Error, CoupaDeliveryLine[]>({
    mutationFn: receiveDelivery,
  });
};

export const useRetryCoupaWithoutStockMutation = () => {
  return useMutation<CoupaReceiveResponse | null, Error, string>({
    mutationFn: retryWithoutStock,
    onSuccess: storeReceiptWithoutStock,
    onError: (error) => {
      refreshCoupaOrders();
      showError(error, "Couldn't check with Coupa.");
    },
  });
};

export const useLinkCoupaOrderLineMutation = () => {
  return useMutation<
    CoupaLineLinkResponse | null,
    Error,
    CoupaLineLinkRequest & { lineId: string }
  >({
    mutationFn: linkOrderLine,
    onSuccess: (response) => {
      if (response?.comparison.length) return;
      if (response?.order) storeCoupaOrder(response.order);
      refreshCoupaOrders();
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.itemLinks() });
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
    },
    onError: (error) => {
      refreshCoupaOrders();
      showError(error, "Couldn't link that line.");
    },
  });
};

function storeRequisition(row: CoupaRequisitionRow | null): void {
  if (!row) return;
  queryClient.setQueryData<CoupaRequisitionRow[] | null>(
    coupaQueryKeys.requisitions(),
    (rows) =>
      rows?.some((existing) => existing.id === row.id)
        ? rows.map((existing) => (existing.id === row.id ? row : existing))
        : [row, ...(rows ?? [])],
  );
}

function storeSentRequisition(
  row: CoupaRequisitionRow | null,
  { punchoutLines }: CoupaRequisitionFields,
): void {
  storeRequisition(row);
  for (const punchoutId of Array.from(
    new Set(punchoutLines.map((line) => line.punchoutId)),
  )) {
    queryClient.removeQueries({
      queryKey: coupaQueryKeys.punchoutCart(punchoutId),
    });
  }
}

function refreshAfterFailedSend(
  error: Error,
  { locationId, shipToAddressId }: CoupaRequisitionFields,
): void {
  queryClient.invalidateQueries({
    queryKey: coupaQueryKeys.requisitions(),
  });
  if (shipToAddressId !== null) {
    queryClient.invalidateQueries({
      queryKey: coupaQueryKeys.shipTo(locationId),
    });
  }
  showError(error, "Couldn't send the requisition.");
}

export const useCreateCoupaRequisitionMutation = () => {
  return useMutation<
    CoupaRequisitionRow | null,
    Error,
    CoupaRequisitionRequest
  >({
    mutationFn: createRequisition,
    onSuccess: storeSentRequisition,
    onError: refreshAfterFailedSend,
  });
};

export const useResendCoupaRequisitionMutation = () => {
  return useMutation<CoupaRequisitionRow | null, Error, CoupaRequisitionResend>(
    {
      mutationFn: resendRequisition,
      onSuccess: storeSentRequisition,
      onError: refreshAfterFailedSend,
    },
  );
};

export const useRemoveCoupaRequisitionMutation = () => {
  return useMutation<void, Error, string>({
    mutationFn: removeRequisition,
    onSuccess: (_, requisitionId) => {
      queryClient.setQueriesData<CoupaRequisitionRow[] | null>(
        { queryKey: coupaQueryKeys.requisitions() },
        (rows) => rows?.filter((row) => row.id !== requisitionId) ?? rows,
      );
    },
    onError: (error) => {
      queryClient.invalidateQueries({
        queryKey: coupaQueryKeys.requisitions(),
      });
      showError(error, "Couldn't remove the requisition.");
    },
  });
};

export const useAddCoupaShipToMutation = () => {
  return useMutation<
    CoupaShipToOption | null,
    Error,
    CoupaShipToAddressRequest
  >({
    mutationFn: addShipTo,
    onSuccess: (option, { locationId }) => {
      if (!option) return;
      queryClient.setQueryData<CoupaShipToOptions | null>(
        coupaQueryKeys.shipTo(locationId),
        (previous) =>
          !previous ||
          previous.addresses.some(
            (address) => address.addressId === option.addressId,
          )
            ? previous
            : {
                ...previous,
                addresses: [
                  ...previous.addresses.filter((address) => address.isClinic),
                  option,
                  ...previous.addresses.filter((address) => !address.isClinic),
                ],
              },
      );
    },
    onError: (error) => showError(error, "Couldn't add the address."),
  });
};

export const useStartCoupaPunchoutMutation = () => {
  return useMutation<CoupaPunchoutStart | null, Error, number>({
    mutationFn: startPunchout,
    onError: (error) => showError(error, "Couldn't open that supplier."),
  });
};

export const useRetryCoupaRequisitionMutation = () => {
  return useMutation<CoupaRequisitionRow | null, Error, string>({
    mutationFn: retryRequisition,
    onSuccess: storeRequisition,
    onError: (error) => {
      queryClient.invalidateQueries({
        queryKey: coupaQueryKeys.requisitions(),
      });
      showError(error, "Couldn't send the requisition.");
    },
  });
};

export const useDismissCoupaPostingMutation = () => {
  return useMutation<CoupaSyncStatus | null, Error, string>({
    mutationFn: dismissPosting,
    onSuccess: (status) => {
      storeSyncStatus(status);
      refreshCoupaOrders();
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
    },
    onError: (error) => showError(error, "Couldn't dismiss that."),
  });
};

export const useApplyCoupaInboundMutation = () => {
  return useMutation<CoupaSyncStatus | null, Error, string>({
    mutationFn: applyInboundRow,
    onSuccess: (status) => {
      storeInboundReview(status);
      refreshCoupaSuppliers();
    },
    onError: (error) => showError(error, "Couldn't apply that change."),
  });
};

export const useDismissCoupaInboundMutation = () => {
  return useMutation<CoupaSyncStatus | null, Error, string>({
    mutationFn: dismissInboundRow,
    onSuccess: storeInboundReview,
    onError: (error) => showError(error, "Couldn't dismiss that."),
  });
};

function refreshStockFromCoupa(): void {
  queryClient.invalidateQueries({ queryKey: coupaQueryKeys.itemLinks() });
  queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
  queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
}

export const useRefreshCoupaReconciliationMutation = () => {
  return useMutation<CoupaReconciliation | null, Error, void>({
    mutationFn: refreshReconciliation,
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(coupaQueryKeys.reconciliation(), data);
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
    },
    onError: (error) => showError(error, "Couldn't compare with Coupa."),
  });
};

export const useFixCoupaDriftMutation = () => {
  return useMutation<CoupaReconciliation | null, Error, CoupaFixDriftRequest>({
    mutationFn: fixReconciliation,
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(coupaQueryKeys.reconciliation(), data);
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
      queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: coupaQueryKeys.reconciliation(),
      });
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.syncStatus() });
    },
  });
};

export const useCoupaGoLiveMutation = () => {
  return useMutation<
    CoupaLocationLinkRow[] | null,
    Error,
    CoupaGoLiveVariables
  >({
    mutationFn: goLive,
    onSuccess: (rows) => {
      storeLocationLinks(rows);
      refreshStockFromCoupa();
    },
    onError: (error, { locationId }) => {
      queryClient.invalidateQueries({
        queryKey: coupaQueryKeys.goLivePreview(locationId),
      });
      return showError(error, "Couldn't take this location live.");
    },
  });
};

export const saveCoupaReasonCodesMutationKey = [
  "coupa",
  "save-reason-codes",
] as const;

export const useSaveCoupaReasonCodesMutation = () => {
  return useMutation<
    CoupaReasonCode[] | null,
    Error,
    { reason: string; code: string | null }
  >({
    mutationKey: saveCoupaReasonCodesMutationKey,
    scope: { id: "coupa-reason-codes" },
    mutationFn: ({ reason, code }) => {
      const saved =
        queryClient.getQueryData<CoupaReasonCode[] | null>(
          coupaQueryKeys.reasonCodes(),
        ) ?? [];
      return saveReasonCodes({
        ...Object.fromEntries(saved.map((row) => [row.reason, row.code])),
        [reason]: code,
      });
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(coupaQueryKeys.reasonCodes(), data);
    },
    onError: (error) => showError(error, "Couldn't save the adjustment codes."),
  });
};

/** Count blind and "Create stock items from Coupa" live on the tenant, so it is read back from there. */
export const useSaveCoupaStockOptionsMutation = () => {
  const { refetchTenant } = useTenant();
  return useMutation<
    CoupaStockOptions | null,
    Error,
    Partial<CoupaStockOptions>
  >({
    mutationFn: saveStockOptions,
    onSuccess: () => refetchTenant(),
    onError: (error) => showError(error, "Couldn't save this setting."),
  });
};

export const useCoupaRequisitions = (enabled: boolean) => {
  const hasCoupa = useModule("COUPA");
  const { data, isLoading, isError } = useQuery({
    ...coupaQueryOptions.requisitions(),
    enabled: hasCoupa && enabled,
  });
  return {
    requisitions: hasCoupa && enabled ? (data ?? []) : [],
    isLoading: hasCoupa && enabled && isLoading,
    isError: hasCoupa && enabled && isError,
  };
};

const toIdSet = (ids: string[] | null) => new Set(ids ?? []);

export const useCoupaLiveLocations = (enabled = true) => {
  const hasCoupa = useModule("COUPA");
  const {
    data: liveLocationIds,
    isSuccess,
    isError,
  } = useQuery({
    ...coupaQueryOptions.liveLocations(),
    enabled: hasCoupa && enabled,
    select: toIdSet,
  });
  return {
    isKnown: !hasCoupa || isSuccess,
    failed: hasCoupa && isError,
    hasLiveLocation: hasCoupa && (!isSuccess || Boolean(liveLocationIds?.size)),
    isLive: (locationId?: string | null) =>
      hasCoupa &&
      (!isSuccess || Boolean(locationId && liveLocationIds?.has(locationId))),
    receivesInCoupa: (locationId?: string | null) =>
      hasCoupa && Boolean(locationId && liveLocationIds?.has(locationId)),
    receivesStockInCoupa: (
      locationId?: string | null,
      stock?: Pick<Stock, "coupaItemId">,
    ) =>
      hasCoupa &&
      Boolean(locationId && liveLocationIds?.has(locationId)) &&
      // Only the stock list and stock details say `coupaItemId: null`; a stock
      // that doesn't carry the field may be linked, so it counts as linked.
      stock?.coupaItemId !== null,
  };
};

export const useCoupaApprovals = () => {
  const hasCoupa = useModule("COUPA");
  const { currentUser } = useUser();
  const enabled =
    hasCoupa &&
    hasPermission(currentUser, PERMISSIONS.INVENTORY_ORDERS_APPROVE);
  const { data, isLoading, isError } = useQuery({
    ...coupaQueryOptions.approvals(),
    enabled,
  });
  return {
    approvals: enabled ? (data ?? []) : [],
    isLoading: enabled && isLoading,
    isError: enabled && isError,
  };
};

function useCoupaApprovalMutation(verb: "approve" | "reject") {
  return useMutation<void, Error, CoupaApprovalAction>({
    mutationFn: (action) => actOnApproval(verb, action),
    onSuccess: (_, { requisitionId }) => {
      queryClient.setQueryData<CoupaApprovalRow[] | null>(
        coupaQueryKeys.approvals(),
        (rows) =>
          rows?.filter((row) => row.coupaRequisitionId !== requisitionId) ??
          rows,
      );
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.approvals() });
      queryClient.invalidateQueries({
        queryKey: coupaQueryKeys.requisitions(),
      });
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: coupaQueryKeys.approvals() });
      showError(error, `Couldn't ${verb} this in Coupa.`);
    },
  });
}

export const useApproveCoupaRequisitionMutation = () =>
  useCoupaApprovalMutation("approve");

export const useRejectCoupaRequisitionMutation = () =>
  useCoupaApprovalMutation("reject");
